import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import ReviewListPopup from "@COMPONENTS/product/common/popup/desktop/ReviewListPopup";
import ReviewPopup from "@COMPONENTS/product/common/popup/desktop/ReviewPopup";
import ReportPopup from "@COMPONENTS/product/common/popup/desktop/ReportPopup";
import * as style from "@COMPONENTS/style/detailpackage/reviews";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {TextMoreButton} from "@FRAMEWORK/ui/desktop/button";
import {StarScore} from "@FRAMEWORK/ui/desktop/flag";
import {Checkbox} from "@FRAMEWORK/ui/desktop/input";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {StackList, StackListItem} from "@FRAMEWORK/ui/desktop/list";
import {SelectBox, SelectLabel, SelectOptions} from "@FRAMEWORK/ui/desktop/select";
import {SubTitle} from "@FRAMEWORK/ui/desktop/text";
import useSigninUser from "@QUERIES/account/useSigninUser";
import usePortalCode from "@QUERIES/portal/usePortalCode";
import useReview, {AttachType, ReviewType} from "@QUERIES/portal/useReview";
import useReviewAI from "@QUERIES/portal/useReviewAI";
import {reviewLikeAsync} from "@QUERIES/portal/useReviewLike";
import useReviewLikeRecommender from "@QUERIES/portal/useReviewLikeRecommender";
import useReviewSummary, {ReviewSummaryType} from "@QUERIES/portal/useReviewSummary";
import useReviewPictures from "@QUERIES/support/useReviewPictures";
import {Noimage} from "@yb/ui/desktop/common/Noimage";
import dayjs from "dayjs";
import {Fragment, useEffect, useMemo, useRef, useState} from "react";

export type GalleryType = ReviewType & {nextReviewId?: number; prevReviewId?: number; attachList: AttachType[]};

const REVIEW_COUNT = 10; // 페이지당 보여줄 갯수
const GALLERY_MAX = 7; // 사진&동영상 최대 갯수
const KEYWORD_MAX = 5; // 키워드 최대 갯수

const reviewSummaryDefault = {
	reviewCnt: 0,
	scoreAvg: 0,
	companion: "",
	companionNm: "",
	companionAttr1: "",
	companionAttr2: "",
	keywordList: [],
};

/** ```
 * 리뷰 썸네일
 * parameter
 *   `AttachType` 첨부파일 타입
 * return
 *  `<img />` 썸네일 이미지
 */
export const ReviewThumbnail = (attach: AttachType & {width?: string; height?: string}) => {
	const {sevSaveDirNm, sevSaveFileNm, vimeoEndPoint, width = "", height = ""} = attach;

	// 이미지 리턴
	const imageUrl =
		sevSaveDirNm && sevSaveFileNm ? `${publicRuntimeConfig.ENV_SCDN_URL}${sevSaveDirNm}/${sevSaveFileNm}` : null;

	const vimeoId = vimeoEndPoint?.split("/")[2] || null;
	const [vimeoUrl, setVimeoUrl] = useState<string>(null);

	const reviewPictruesFetch = useReviewPictures(vimeoId, {enabled: !!vimeoId});
	const reviewPictrues = reviewPictruesFetch.data?.body || null;

	useEffect(() => {
		if (reviewPictrues) setVimeoUrl(reviewPictrues?.sizes?.[2]?.link_with_play_button);
	}, [reviewPictrues]);
	return <Noimage src={imageUrl || vimeoUrl} width={width} height={height} />;
};

const ReviewComponent = ({evCd}: {evCd: string}) => {
	const goodsCd = evCd.split("-")[0];
	const [open] = useOverlay();

	// 유저 정보 조회
	const signin = useSigninUser();
	const signinCode = signin.data?.code || "9999";

	// 리뷰 리스트 조회
	const reviewDataFetch = useReview(goodsCd);
	const reviewData = reviewDataFetch.data?.body || null;

	// 리뷰 데이터 정제
	const originReview: GalleryType[] = useMemo(() => {
		if (!reviewData) return [];
		const {reviewList, attachList: attach} = reviewData;
		return reviewList.reduce((acc, cur) => {
			const attachList = attach.filter(v => v.reviewId === cur.reviewId);
			const item = {...cur, attachList};
			acc.push(item);
			return acc;
		}, []);
	}, [reviewData]);

	// 리뷰 좋아요 조회 (로그인 유저)
	const reviewLikeRecommenderFetch = useReviewLikeRecommender(goodsCd, {enabled: signinCode === "0000"});
	const reviewLikeRecommender = reviewLikeRecommenderFetch.data?.body || [];

	// 리뷰 서머리 조회
	const reviewSummaryFetch = useReviewSummary(goodsCd);
	const reviewSummary = reviewSummaryFetch.data?.body || (reviewSummaryDefault as ReviewSummaryType);

	// 리뷰 AI 조회
	const reviewAIFetch = useReviewAI(goodsCd);
	const reviewAI = reviewAIFetch.data?.body;

	// 공통코드 조회 (동행인 코드)
	const companionCodeFetch = usePortalCode("REVIEW_COMPANION");
	const companionCode = companionCodeFetch.data?.body || [];

	// 공통코드 조회 (나이 코드)
	const ageCodeFetch = usePortalCode("REVIEW_AGE");
	const ageCode = ageCodeFetch.data?.body || [];

	// AI 여행 분석 툴팁
	const [showTooltip, setShowTooltip] = useState(false);
	const handleMouseEnter = () => setShowTooltip(true);
	const handleMouseLeave = () => setShowTooltip(false);

	const [page, setPage] = useState(1);

	// 리뷰 SORT
	const rating = [
		{text: "별점순", value: "score", asc: false},
		{text: "최신순", value: "reviewDt", asc: false},
		{text: "추천순", value: "likeCnt", asc: false},
	];
	const [selectedRate, setSelectedRate] = useState(rating[0]);

	// 사진 동영상 리뷰만 모아보기 체크박스
	const [isAttach, setIsAttach] = useState(false);

	// 필터 : 동행인 셀렉트 박스
	const [showCompanion, setShowCompanion] = useState(false);
	const [currentCompanion, setCurrentCompanion] = useState({key: "ALL", text: "동행인 전체"});

	// 필터 : 연령대 셀렉트 박스
	const [showAge, setShowAge] = useState(false);
	const [currentAge, setCurrentAge] = useState({key: "ALL", text: "연령대 전체"});

	const handleCompanion = e => {
		const text = e.target.innerText;
		const key = e.target.getAttribute("value");
		setCurrentCompanion({key, text});
		setShowCompanion(show => !show);
	};
	const handleAge = e => {
		const text = e.target.innerText;
		const key = e.target.getAttribute("value");
		setCurrentAge({key, text});
		setShowAge(show => !show);
	};

	// 동행인, 연령대 셀렉트 외 영역 클릭시 닫기
	useEffect(() => {
		const event = e => {
			if (document.body.contains(e.target)) {
				!e.target.closest(`#${CSS.escape("__companion")}`) && setShowCompanion(false);
				!e.target.closest(`#${CSS.escape("__age")}`) && setShowAge(false);
			}
		};
		setTimeout(() => window.addEventListener("click", event));
		return () => {
			window.removeEventListener("click", event);
		};
	}, [showCompanion, showAge]);

	/** 키워드 리스트 */
	const keywords = useMemo(() => {
		if (!reviewSummary || !reviewSummary.keywordList) return [];
		const result = reviewSummary.keywordList || [];
		// keywordCnt 기준으로 내림차순 정렬
		result.sort((a, b) => b.keywordCnt - a.keywordCnt);
		return result.slice(0, KEYWORD_MAX);
	}, [reviewSummary]);

	/** 사진&동영상 리스트 */
	const galleries = useMemo(() => originReview?.filter(v => v.attachList?.length > 0) ?? [], [originReview]);

	/** 필터된 리뷰 리스트 */
	const firteredReviews = useMemo(() => {
		if (page !== 1) setPage(1); // 필터링 시 페이지 초기화
		return [...originReview].filter(
			v =>
				(currentCompanion.key === "ALL" || v.companion === currentCompanion.key) &&
				(currentAge.key === "ALL" || v.age === currentAge.key) &&
				(!isAttach || v.attachList?.length > 0), // isAttach가 true면 attachList가 있는 경우만 필터링
		);
	}, [currentCompanion, currentAge, isAttach]);

	/** 리뷰 리스트 */
	const reviews = useMemo(
		() =>
			[...originReview]
				.filter(
					v =>
						(currentCompanion.key === "ALL" || v.companion === currentCompanion.key) &&
						(currentAge.key === "ALL" || v.age === currentAge.key) &&
						(!isAttach || v.attachList?.length > 0), // isAttach가 true면 attachList가 있는 경우만 필터링
				)
				.sort((a, b) => {
					if (a[selectedRate.value] < b[selectedRate.value]) return selectedRate.asc ? -1 : 1;
					if (a[selectedRate.value] > b[selectedRate.value]) return selectedRate.asc ? 1 : -1;
					return dayjs(b.reviewDt).valueOf() - dayjs(a.reviewDt).valueOf(); // 최신순 정렬
				})
				.slice(0, page * REVIEW_COUNT),
		[originReview, currentCompanion, currentAge, selectedRate, isAttach, page],
	);

	// 텍스트 더보기 버튼
	const [expandedReviewIds, setExpandedReviewIds] = useState([]);
	const [showMoreButton, setShowMoreButton] = useState({});
	const textRefs = useRef({});

	useEffect(() => {
		originReview.forEach(review => {
			const textElement = textRefs.current[review.reviewId];
			if (textElement) {
				const lineHeight = parseInt(getComputedStyle(textElement).lineHeight, 10);
				const maxHeight = lineHeight * 3; // 3줄 높이 계산

				if (textElement.scrollHeight > maxHeight) {
					setShowMoreButton(prev => ({...prev, [review.reviewId]: true}));
				} else {
					setShowMoreButton(prev => ({...prev, [review.reviewId]: false}));
				}
			}
		});
	}, [currentCompanion, currentAge, selectedRate, isAttach, page]);

	// 더보기 접기 버튼
	const toggleExpand = id => {
		setExpandedReviewIds(prev => (prev.includes(id) ? prev.filter(reviewId => reviewId !== id) : [...prev, id]));
	};

	// 상세 팝업
	const [detailReviewId, setDetailReviewId] = useState<number>(null);
	const [optionAttach, setOptionAttach] = useState<AttachType>(null); // 선택한 이미지 default
	const handleReviewOpen = (reviewId: number, attach: AttachType = null) => {
		setDetailReviewId(reviewId);
		setOptionAttach(attach);
	};
	const handleReviewClose = () => setDetailReviewId(null);

	// 리스트 팝업
	const [isListPopup, setIsListPopup] = useState(false);
	const handleReviewListOpen = () => setIsListPopup(true);
	const handleReviewListClose = () => setIsListPopup(false);

	// 추천하기(좋아요) 버튼
	const handleLike = (reviewId: number) => {
		if (signinCode === "0000") {
			reviewLikeAsync({goodsCd, reviewId: reviewId.toString()}).then(result => {
				if (result.data.code === "0000") {
					reviewDataFetch.refetch();
					reviewLikeRecommenderFetch.refetch();
				}
			});
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					resolve={value => !!value && LoginUtil.login()}
				/>
			));
		}
	};

	// 신고하기 버튼
	const handleReport = (review: GalleryType) => {
		const {reportYn, reviewId} = review;
		if (reportYn === "Y") {
			open(prop => <Alert {...prop} text="신고사유 검토중인 게시물입니다.<br/>빠른 시일 내에 처리하겠습니다." />);
			return;
		}

		if (signinCode === "0000") {
			open(overlay => <ReportPopup {...overlay} reportId={reviewId} reportType="REVIEW" />);
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					resolve={value => !!value && LoginUtil.login()}
				/>
			));
		}
	};

	return (
		<>
			<style.SubTitleWrap>
				<SubTitle>고객리뷰 및 여행후기</SubTitle>
			</style.SubTitleWrap>
			<style.CategoryTitle $line>{`고객리뷰(${reviewSummary.reviewCnt})`}</style.CategoryTitle>
			{originReview.length > 0 && (
				<>
					<style.Box>
						<Flex direction="column" align="center" gap="20">
							<style.Rating aria-label={`평점 ${reviewSummary.scoreAvg}점`}>
								<span>{reviewSummary.scoreAvg}</span> / <em>5</em>
							</style.Rating>
							<style.CustomerReview>
								총 <em>{reviewSummary.reviewCnt}</em>명의 고객님이 이 여행을 이렇게 평가해주셨어요.
							</style.CustomerReview>
							<style.TripLabel>{reviewSummary.companionAttr1}</style.TripLabel>
						</Flex>
						{/* AI - INFO */}
						{reviewSummary.reviewCnt >= 10 && reviewAI && (
							<>
								<style.Paragraph>
									<style.SubHeadingWrap>
										<style.CategoryTitle>
											<span>AI</span> 여행 분석
										</style.CategoryTitle>
										<style.ToolTipBtn onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
											<span>AI 리뷰 요약</span>
											{showTooltip && (
												<style.ToolTip>
													<p>AI 리뷰 요약이란?</p>
													<ul>
														<li>- 많은 양의 고객 리뷰를 AI가 요점만 요약해 드립니다.</li>
														<li>- 요약 내용은 주기적으로 업데이트됩니다.</li>
													</ul>
												</style.ToolTip>
											)}
										</style.ToolTipBtn>

										<style.Percent>
											긍정 평가 <em>{reviewAI.emotion}</em>%
										</style.Percent>
									</style.SubHeadingWrap>
									<style.Summary>
										<em>이 여행의 리뷰를 요약하면,</em>
										<p>{reviewAI.summary}</p>
										<style.Tag>
											{reviewAI.keyword
												.split(" ")
												.slice(0, 5)
												.map((tag, index) => (
													<li key={index}>{tag.replace("#", "")}</li>
												))}
										</style.Tag>
									</style.Summary>
								</style.Paragraph>
							</>
						)}
						{keywords.length > 0 && (
							<style.Paragraph>
								<style.CategoryTitle>이 여행은 이런 점이 좋아요</style.CategoryTitle>
								<style.Highlights>
									{keywords.map((item, index) => {
										const {keyword, keywordCnt} = item;
										return (
											<style.HighlightItem key={index}>
												{keyword}
												<span>{keywordCnt}</span>명
											</style.HighlightItem>
										);
									})}
								</style.Highlights>
							</style.Paragraph>
						)}
					</style.Box>

					{/* 사진 & 동영상 */}
					{galleries.length > 0 && (
						<>
							<style.CategoryTitle>사진&amp;동영상</style.CategoryTitle>
							<style.Gallery>
								{galleries.slice(0, GALLERY_MAX).map((gallery, galleryIndex) => (
									<style.GalleryItem key={gallery.reviewId} $isList={galleryIndex + 1 === GALLERY_MAX}>
										<a
											href="#"
											onClick={e => {
												e.preventDefault();
												handleReviewListOpen();
											}}
										>
											<ReviewThumbnail {...gallery.attachList[0]} />
										</a>
									</style.GalleryItem>
								))}
							</style.Gallery>
						</>
					)}
					<style.ReviewFilter>
						<Flex>
							<Flex gap="10">
								{/* 동행인 필터 */}
								<style.SelectWrap id="__companion">
									<SelectBox onClick={() => setShowCompanion(!showCompanion)}>
										<SelectLabel>{currentCompanion.text}</SelectLabel>
									</SelectBox>
									{showCompanion && (
										<SelectOptions>
											<li onClick={handleCompanion} value={"ALL"}>
												동행인 전체
											</li>
											{companionCode.map(companion => {
												const {commCdId, commCdNm} = companion;
												return (
													<li onClick={handleCompanion} key={commCdId} value={commCdId}>
														{commCdNm}
													</li>
												);
											})}
										</SelectOptions>
									)}
								</style.SelectWrap>
								{/* 나이 필터 */}
								<style.SelectWrap id="__age">
									<SelectBox onClick={() => setShowAge(!showAge)}>
										<SelectLabel>{currentAge.text}</SelectLabel>
									</SelectBox>
									{showAge && (
										<SelectOptions>
											<li onClick={handleAge} value={"ALL"}>
												연령대 전체
											</li>
											{ageCode.map(age => {
												const {commCdId, commCdNm} = age;
												return (
													<li onClick={handleAge} key={commCdId} value={commCdId}>
														{commCdNm}
													</li>
												);
											})}
										</SelectOptions>
									)}
								</style.SelectWrap>
							</Flex>
							{/* 사진&동영상 모아보기 */}
							<style.CheckboxWrap>
								<Checkbox id="check-attach" onChange={() => setIsAttach(!isAttach)} checked={isAttach} />
								<label htmlFor="check-attach">사진&amp;동영상 리뷰만 모아보기</label>
							</style.CheckboxWrap>
						</Flex>
						{/* SORT */}
						<StackList cursor={"pointer"}>
							{rating.map(rate => (
								<StackListItem
									key={rate.value}
									onClick={() => setSelectedRate(rate)}
									isActive={selectedRate.value === rate.value}
								>
									{rate.text}
								</StackListItem>
							))}
						</StackList>
					</style.ReviewFilter>
				</>
			)}
			<ul>
				{reviews.slice(0, page * REVIEW_COUNT).map(review => {
					const {score, custNm, ageNm, companionNm, reviewDt, keywords, attachList, detail} = review;
					const {likeCnt, reviewId} = review;
					return (
						<style.ReviewListItem key={reviewId}>
							<style.ReviewTopBar>
								<style.ScoreWrap>
									<StarScore>
										<span style={{width: `calc(${Math.round(score)} * 20%)`}} />
									</StarScore>
									<em>{Math.round(score)}</em>
								</style.ScoreWrap>
								<style.ReviewerProfile>
									<style.UserName>{custNm ?? " - "}님</style.UserName>
									<style.ProfileDetails>
										{ageNm && <span>{ageNm}</span>}
										<span>{companionNm}</span>
									</style.ProfileDetails>
								</style.ReviewerProfile>
								<style.Date dateTime={dayjs(reviewDt).format("YYYY.MM.DD")}>
									{dayjs(reviewDt).format("YYYY.MM.DD")}
								</style.Date>
							</style.ReviewTopBar>
							{keywords && (
								<style.Highlights>
									{keywords.split(",").map((keyword, index) => (
										<Fragment key={index}>{keyword && <style.HighlightItem>{keyword}</style.HighlightItem>}</Fragment>
									))}
								</style.Highlights>
							)}

							{attachList && (
								<style.ReviewGallery>
									{attachList.map((attach, index) => (
										<style.ReviewGalleryItem key={index}>
											<a
												href="#"
												onClick={e => {
													e.preventDefault();
													handleReviewOpen(reviewId, attach);
												}}
											>
												<ReviewThumbnail {...attach} />
											</a>
										</style.ReviewGalleryItem>
									))}
								</style.ReviewGallery>
							)}

							<style.ReviewText
								ref={el => (textRefs.current[reviewId] = el)}
								isShow={expandedReviewIds.includes(reviewId)}
								dangerouslySetInnerHTML={{__html: detail.replace(/\n/g, "<br />")}}
							/>

							{showMoreButton[reviewId] && (
								<TextMoreButton onClick={() => toggleExpand(reviewId)} isShow={expandedReviewIds.includes(reviewId)} />
							)}

							<style.FeedbackControls>
								<style.RecommendBtnWrap onClick={() => handleLike(reviewId)}>
									<style.RecommendBtnIcon $isActive={!!reviewLikeRecommender.find(v => v.reviewId === reviewId)} />
									<style.RecommendBtn>추천</style.RecommendBtn>
									<style.LikeCount>{likeCnt}</style.LikeCount>
								</style.RecommendBtnWrap>
								<style.ReportBtn onClick={() => handleReport(review)}>신고하기</style.ReportBtn>
							</style.FeedbackControls>
						</style.ReviewListItem>
					);
				})}
			</ul>
			{page * REVIEW_COUNT < firteredReviews.length && (
				<style.LoadMoreBtn onClick={() => setPage(page + 1)}>
					<em>
						더보기({page * REVIEW_COUNT}/{firteredReviews.length})
					</em>
				</style.LoadMoreBtn>
			)}
			{/* 리뷰가 없을경우 */}
			{originReview.length === 0 && <style.WriteRequest>아직 작성된 리뷰가 없습니다.</style.WriteRequest>}
			{/* 조건에 맞는 리뷰가 없을경우 */}
			{originReview.length !== 0 && reviews.length === 0 && (
				<style.WriteRequest>
					조건에 맞는 리뷰가 없습니다.
					<br /> 필터를 변경하여 다른 조건으로 검색해보세요.
				</style.WriteRequest>
			)}
			{/* 리뷰 상세 팝업 */}
			{detailReviewId && (
				<ReviewPopup
					optionAttach={optionAttach}
					reviewId={detailReviewId}
					goodsCd={goodsCd}
					close={handleReviewClose}
					listOpen={handleReviewListOpen}
				/>
			)}
			{/* 리뷰 리스트 팝업 */}
			{isListPopup && <ReviewListPopup goodsCd={goodsCd} close={handleReviewListClose} reviewOpen={handleReviewOpen} />}
		</>
	);
};

const Review = ({evCd}: {evCd: string}) => (
	<ErrorBoundary>
		<ReviewComponent evCd={evCd} />
	</ErrorBoundary>
);

export default Review;
